import { observable, action, ObservableMap, computed } from "mobx";
import { DbTabBeingRecorded, UUID } from "./DatabaseTypes";
import { requireEvidenceDatabase } from "./getDatabase";
import { TabStartedRecording, TabStoppedRecording } from "../StateEvents";
import { fromPromise } from "mobx-utils";
import { EvidenceDatabase } from "./EvidenceDbStruct";


export class TabsBeingRecorded {
	_tabsBeingRecordedCache: ObservableMap<number, DbTabBeingRecorded>;

	#addTabBeingRecordedLocally = action( (...tabsBeingRecorded: DbTabBeingRecorded[]) => {
		for (const tabBeingRecorded of tabsBeingRecorded) {
			this._tabsBeingRecordedCache.set(tabBeingRecorded.tabId, tabBeingRecorded);
		}
		// ProcessInfo.log(`#addTabBeingRecordedLocally (${tabsBeingRecorded.map( t => t.tabId).join(", ")})`, [...this._tabsBeingRecordedCache.keys()]);
	});

	#removeTabBeingRecordedLocally = action( (tabId: number) => {
		this._tabsBeingRecordedCache.delete(tabId);
		// ProcessInfo.log(`#removeTabBeingRecordedLocally (${tabId})`, [...this._tabsBeingRecordedCache.keys()]);
	});

	get = (tabId: number | undefined) => tabId == null ? undefined : this._tabsBeingRecordedCache.get(tabId);
	has = (tabId: number | undefined) => tabId == null ? false : this._tabsBeingRecordedCache.has(tabId);

	static get = async (tabId: number | undefined) => {
		return (await TabsBeingRecorded.cachedTablePromise).get(tabId);
	}
	static has = async (tabId: number | undefined) => {
		return (await TabsBeingRecorded.cachedTablePromise).has(tabId);
	}

	@computed get all() {
		return [...this._tabsBeingRecordedCache.values()];
	}

	protected constructor(public readonly db: EvidenceDatabase, tabsBeingRecorded: Iterable<DbTabBeingRecorded>) {
		this._tabsBeingRecordedCache = observable.map([...tabsBeingRecorded].map( tab => [tab.tabId, tab] as const));
		TabStoppedRecording.addListener( ({tabId}) => this.#removeTabBeingRecordedLocally(tabId));
		TabStartedRecording.addListener( ({messageType: _, ...dbTabBeingRecorded}) => {
			// ProcessInfo.log("Received TabStartedRecording", {messageType, ...dbTabBeingRecorded});
			 this.#addTabBeingRecordedLocally(dbTabBeingRecorded);
		});
	}

	static add = async (dbTabBeingRecorded: DbTabBeingRecorded) => {
		const db = await requireEvidenceDatabase();
		await db.put("tabsBeingRecorded", dbTabBeingRecorded);
		// ProcessInfo.log("Added TabStartedRecording", dbTabBeingRecorded);
		TabStartedRecording.send(dbTabBeingRecorded);
	}

	static remove = async (tabId: number) => {
		const db = await requireEvidenceDatabase();
		await db.delete("tabsBeingRecorded", tabId);
		TabStoppedRecording.send({tabId});
	}

	static removeAll= async () => {
		const db = await requireEvidenceDatabase();
		const t = db.transaction("tabsBeingRecorded", "readwrite");
		const tabIds = await t.store.getAllKeys();
		for (const tabId of tabIds) {
			t.store.delete(tabId);
		}
		t.commit();
	}

	static getAllForRecording = async ({recordingUuid}: {recordingUuid: UUID}) => {
		const db = await requireEvidenceDatabase();
		const t = db.transaction("tabsBeingRecorded", "readonly");
		const tabIds = await t.store.index("TabBeingRecordedTable-Index-RecordingUuid").getAllKeys(IDBKeyRange.only(recordingUuid));
		t.commit();
		return tabIds;
	}

	static removeAllForRecording = async ({recordingUuid}: {recordingUuid: UUID}) => {
		const db = await requireEvidenceDatabase();
		const t = db.transaction("tabsBeingRecorded", "readwrite");
		const tabIds = await t.store.index("TabBeingRecordedTable-Index-RecordingUuid").getAllKeys(IDBKeyRange.only(recordingUuid));
		for (const tabId of tabIds) {
			t.store.delete(tabId);
		}
		t.commit();
		// ProcessInfo.log(`Removed all tabs being recorded for recording ${recordingUuid}`, tabIds);
		for (const tabId of tabIds) {
			TabStoppedRecording.send({tabId});
		}
	}

	addTabBeingRecorded = (dbTabBeingRecorded: DbTabBeingRecorded) => TabsBeingRecorded.add(dbTabBeingRecorded);
	removeTabBeingRecorded = (tabId: number) => TabsBeingRecorded.remove(tabId);
	removeAllTabsBeingRecordedForRecording = (recordingUuid: UUID) => TabsBeingRecorded.removeAllForRecording({recordingUuid});

	protected static async create () {
		const db = await requireEvidenceDatabase();
		const t = db.transaction("tabsBeingRecorded", "readonly");
		const tabsBeingRecorded = await t.store.getAll();
		t.commit();
		return new TabsBeingRecorded(db, tabsBeingRecorded);
	}

	static #cachedTablePromise: Promise<TabsBeingRecorded> | undefined;
	static get cachedTablePromise() {
		return TabsBeingRecorded.#cachedTablePromise ??= this.create()
	}
	@computed static get cachedTable() {
		const fp = fromPromise(TabsBeingRecorded.cachedTablePromise);
		return fp.state === "fulfilled" ? fp.value : undefined;
	}

}
